<template>
  <div class="min-h-screen bg-white text-[#202632]">
    <MediaHubHeader />

    <section class="border-b border-[#e0e0e0] bg-[#fbfbfc]">
      <div class="mx-auto flex w-full max-w-[1140px] flex-col gap-3 px-4 py-10 lg:px-0">
        <router-link to="/" class="w-fit text-sm font-semibold text-[#8a8f99] hover:text-[#d9a72e]">미디어허브</router-link>
        <h1 class="text-3xl font-bold sm:text-4xl">검색 결과</h1>
        <p class="text-sm text-[#5f6673]">
          <strong class="text-[#202632]">{{ keyword }}</strong>
          검색어가 포함된 노출 콘텐츠를 확인합니다.
        </p>
      </div>
    </section>

    <main class="mx-auto flex w-full max-w-[1140px] flex-col gap-8 px-4 py-10 lg:px-0">
      <section
        class="flex flex-col gap-4 rounded-2xl border border-[#e6e8ee] bg-[#fbfbfc] p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="filter-grid">
          <select v-model.number="size" class="filter-select" @change="changePageSize">
            <option v-for="option in sizeOptions" :key="option" :value="option">{{ option }}개씩</option>
          </select>
          <select v-model="sort" class="filter-select" @change="changeSort">
            <option value="latest">최신순</option>
            <option value="views">조회순</option>
          </select>
          <select v-model="sourceType" class="filter-select filter-select-source" @change="changeSource">
            <option value="">전체</option>
            <option v-for="source in sourceFilters" :key="source.sourceType" :value="source.sourceType">
              {{ source.mediaSourceName }}
            </option>
          </select>
        </div>
        <div class="text-sm font-semibold text-[#5f6673]">총 {{ total }}개 콘텐츠</div>
      </section>

      <section v-if="loading" class="py-20 text-center text-[#5f6673]">콘텐츠를 불러오고 있습니다.</section>
      <section v-else-if="errorMessage" class="py-20 text-center">
        <p class="font-bold">콘텐츠를 불러오지 못했습니다.</p>
        <p class="mt-2 text-sm text-[#8a8f99]">{{ errorMessage }}</p>
      </section>
      <section
        v-else-if="!items.length"
        class="rounded-2xl border border-[#e0e0e0] bg-[#f9f9f9] px-6 py-16 text-center text-[#6b7280]"
      >
        검색 결과가 없습니다.
      </section>
      <section v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <a
          v-for="content in items"
          :key="content.homepageContentCode"
          :href="contentGoUrl(content.homepageContentCode)"
          target="_blank"
          rel="noopener noreferrer"
          class="group flex min-h-[360px] flex-col overflow-hidden rounded-xl bg-white shadow-[4px_4px_14px_rgba(0,0,0,0.14)] transition hover:-translate-y-1 hover:shadow-[4px_8px_24px_rgba(0,0,0,0.2)]"
        >
          <div class="relative h-[204px] w-full overflow-hidden bg-[#e9e9e9]">
            <img
              :src="content.thumbnailUrl || fallbackImage(content)"
              alt=""
              class="h-full w-full object-cover transition group-hover:scale-105"
            />
            <span class="absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white">
              {{ contentTypeLabel(content.contentType) }}
            </span>
          </div>
          <div class="flex flex-1 flex-col gap-3 p-5">
            <div v-if="content.displayCategoryName" class="flex">
              <span class="rounded-full border border-[#d9a72e]/40 bg-[#fff8e6] px-3 py-1 text-xs font-bold text-[#b38314]">
                {{ content.displayCategoryName }}
              </span>
            </div>
            <h2 class="line-clamp-2 text-lg font-bold leading-[1.45] text-[#3a4250] sm:text-xl">{{ content.title }}</h2>
            <p v-if="content.description" class="line-clamp-2 text-sm leading-[1.6] text-[#6b7280]">
              {{ content.description }}
            </p>
            <div class="mt-auto flex flex-wrap items-center gap-2 text-sm text-[#8a8f99]">
              <span>{{ content.authorName || content.mediaSourceName || content.mediaHubCategoryName }}</span>
              <span v-if="formatDate(content.publishedDt || content.fetchedDt)">·</span>
              <span>{{ formatDate(content.publishedDt || content.fetchedDt) }}</span>
              <span>·</span>
              <span>조회 {{ content.viewCount || 0 }}</span>
            </div>
          </div>
        </a>
      </section>

      <nav v-if="totalPages > 1" class="flex items-center justify-center gap-2 pt-4">
        <button type="button" class="page-button" :disabled="page <= 1" @click="movePage(page - 1)">이전</button>
        <button
          v-for="pageNo in pageNumbers"
          :key="pageNo"
          type="button"
          class="page-number"
          :class="{ active: pageNo === page }"
          @click="movePage(pageNo)"
        >
          {{ pageNo }}
        </button>
        <button type="button" class="page-button" :disabled="page >= totalPages" @click="movePage(page + 1)">다음</button>
      </nav>
    </main>
  </div>
</template>

<script>
import { getMediaHubContentGoUrl, searchMediaHubContents } from '@/api/mediaHub'
import MediaHubHeader from '@/components/MediaHubHeader.vue'
import imgImage64 from '@/assets/figma/home/imgImage64.png'
import imgImage65 from '@/assets/figma/home/imgImage65.png'
import imgImage66 from '@/assets/figma/home/imgImage66.png'

const fallbackImages = [imgImage64, imgImage65, imgImage66]

export default {
  name: 'MediaSearchView',
  components: { MediaHubHeader },
  data() {
    return {
      items: [],
      sourceFilters: [],
      total: 0,
      totalPages: 0,
      keyword: this.$route.query.keyword || '',
      page: Number(this.$route.query.page || 1),
      size: Number(this.$route.query.size || 10),
      sort: this.$route.query.sort || 'latest',
      sourceType: this.$route.query.sourceType || '',
      sizeOptions: [10, 20, 30, 50, 100],
      loading: false,
      errorMessage: '',
    }
  },
  computed: {
    pageNumbers() {
      const max = 5
      const half = Math.floor(max / 2)
      let start = Math.max(1, this.page - half)
      const end = Math.min(this.totalPages, start + max - 1)
      start = Math.max(1, end - max + 1)
      return Array.from({ length: end - start + 1 }, (_, index) => start + index)
    },
  },
  watch: {
    '$route.fullPath'() {
      this.syncFromRoute()
      this.loadContents()
    },
  },
  mounted() {
    this.loadContents()
  },
  methods: {
    syncFromRoute() {
      this.keyword = this.$route.query.keyword || ''
      this.page = Number(this.$route.query.page || 1)
      this.size = Number(this.$route.query.size || 10)
      this.sort = this.$route.query.sort || 'latest'
      this.sourceType = this.$route.query.sourceType || ''
    },
    async loadContents() {
      this.loading = true
      this.errorMessage = ''
      try {
        const data = await searchMediaHubContents({
          keyword: this.keyword,
          page: this.page,
          size: this.size,
          sort: this.sort,
          sourceType: this.sourceType,
        })
        this.items = data.items || []
        this.sourceFilters = data.sourceFilters || []
        this.total = data.total || 0
        this.totalPages = data.totalPages || 0
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.loading = false
      }
    },
    updateQuery(next) {
      this.$router.push({
        name: 'media-search',
        query: {
          keyword: next.keyword ?? this.keyword,
          page: next.page || this.page,
          size: next.size || this.size,
          sort: next.sort || this.sort,
          ...(next.sourceType || this.sourceType ? { sourceType: next.sourceType ?? this.sourceType } : {}),
        },
      })
    },
    movePage(page) {
      if (page < 1 || page > this.totalPages || page === this.page) return
      this.updateQuery({ page })
    },
    changePageSize() {
      this.updateQuery({ page: 1, size: this.size })
    },
    changeSort() {
      this.updateQuery({ page: 1, sort: this.sort })
    },
    changeSource() {
      this.updateQuery({ page: 1, sourceType: this.sourceType })
    },
    contentGoUrl(homepageContentCode) {
      return getMediaHubContentGoUrl(homepageContentCode)
    },
    contentTypeLabel(type) {
      const labels = {
        NEWS: '뉴스',
        VIDEO: '동영상',
        SHORTS: '쇼츠',
        CLIP: '클립',
        BLOG: '블로그',
        CAFE_ARTICLE: '카페글',
        PRODUCT: '상품정보',
        NOTICE: '공지/이벤트',
        LIVE: '라이브',
        POST: '게시글',
        ETC: '기타',
      }
      return labels[type] || type || '콘텐츠'
    },
    formatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date)
    },
    fallbackImage(content) {
      const key = content.homepageContentCode || content.fetchCode || ''
      const index = Math.abs([...key].reduce((sum, char) => sum + char.charCodeAt(0), 0)) % fallbackImages.length
      return fallbackImages[index]
    },
  },
}
</script>

<style scoped>
.filter-select {
  width: 100%;
  min-width: 0;
  height: 42px;
  border: 1px solid #d9dde5;
  border-radius: 10px;
  background: #fff;
  padding: 0 42px 0 14px;
  color: #202632;
  font-weight: 700;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9L12 15L18 9' stroke='%23202632' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 14px 14px;
}

.filter-grid {
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
}

.filter-select-source {
  grid-column: 1 / -1;
}

.filter-select:focus {
  border-color: #d9a72e;
}

@media (min-width: 640px) {
  .filter-grid {
    width: auto;
    grid-template-columns: 136px 136px minmax(220px, 344px);
  }

  .filter-select-source {
    grid-column: auto;
  }
}

.page-button,
.page-number {
  min-width: 40px;
  height: 40px;
  border: 1px solid #d9dde5;
  border-radius: 10px;
  background: #fff;
  color: #3a4250;
  font-weight: 800;
}

.page-button {
  padding: 0 14px;
}

.page-button:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.page-number.active {
  border-color: #d9a72e;
  background: #d9a72e;
  color: #fff;
}
</style>
